import React, {useEffect, useState, useCallback, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Switch,
  Modal,
  useWindowDimensions,
  Platform,
  AppState,
  AppStateStatus,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {settingsService, calendarService} from '../services';
import {openOAuthUrl} from '../services/oauthBrowser';
import {OAuthStatus} from '../services/settingsService';
import {Calendar, GoogleCalendar, Reminder} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {useRouting} from '../hooks/useRouting';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {
  SettingsLayout,
  Heading,
  SubHeading,
  BodyText,
  Button,
  ConnectServiceCard,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';

interface OAuthWindow {
  location: { href: string };
  close: () => void;
}

declare const window: {
  location: { href: string; pathname: string };
  history: { replaceState: (data: any, title: string, url: string) => void };
  open: (url: string, target?: string) => OAuthWindow | null;
};

interface GroupedReminders {
  today: Reminder[];
  tomorrow: Reminder[];
  thisWeek: Reminder[];
  later: Reminder[];
}

export default function CalendarSettingsScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen} = useSettingsNavigation('CalendarSettings');
  const dispatch = useAppDispatch();
  const {width} = useWindowDimensions();
  const isTwoColumn = width >= 900;
  const {getSearchParam, clearSearchParams} = useRouting();

  const [oauthStatus, setOAuthStatus] = useState<OAuthStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [availableCalendars, setAvailableCalendars] = useState<GoogleCalendar[]>([]);
  const [userCalendars, setUserCalendars] = useState<Calendar[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isAddingCalendar, setIsAddingCalendar] = useState<string | null>(null);
  const [showAddCalendarDropdown, setShowAddCalendarDropdown] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Track if we're waiting for OAuth to complete (native only)
  const isWaitingForOAuth = useRef(false);
  const appState = useRef(AppState.currentState);

  const loadData = useCallback(async () => {
    const status = await settingsService.getOAuthStatus();
    setOAuthStatus(status);

    if (status.calendarConnected) {
      const [available, user, events] = await Promise.all([
        calendarService.getAvailableCalendars(),
        calendarService.getUserCalendars(),
        calendarService.getReminders(14),
      ]);
      setAvailableCalendars(available);
      setUserCalendars(user);
      setReminders(events);
    }
  }, []);

  const {isLoading, loadError, isOnline, refreshControlProps, refresh} = useDataLoader(loadData);

  // Refresh data when app returns to foreground after OAuth (native only)
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active' &&
        isWaitingForOAuth.current
      ) {
        // App came back to foreground after OAuth
        isWaitingForOAuth.current = false;
        refresh();
      }
      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription.remove();
  }, [refresh]);

  // Clean up OAuth query params from URL and refresh data
  useEffect(() => {
    const oauthSuccess = getSearchParam('oauth_success');
    const oauthError = getSearchParam('oauth_error');
    if (oauthSuccess || oauthError) {
      clearSearchParams();
      // Refresh data after OAuth redirect
      if (oauthSuccess) {
        refresh();
      }
    }
  }, [getSearchParam, clearSearchParams, refresh]);

  const handleLogout = async () => {
    await dispatch(logout());
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const events = await calendarService.getReminders(14);
      setReminders(events);
    } catch (error) {
      console.error('Failed to sync', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConnect = async () => {
    try {
      const platform = Platform.OS === 'web' ? 'web' : 'native';
      const {authorizationUrl} = await settingsService.getCalendarAuthUrl(platform);

      if (Platform.OS === 'web') {
        // On web, open in new window
        const authWindow = window.open('about:blank', '_blank');
        if (authWindow) {
          authWindow.location.href = authorizationUrl;
        }
      } else {
        // On native, open system browser - app will return via deep link
        isWaitingForOAuth.current = true;
        await openOAuthUrl(authorizationUrl);
        // Don't refresh here - will be triggered by AppState change or deep link
      }
    } catch (error) {
      console.error('Failed to get auth URL', error);
      isWaitingForOAuth.current = false;
    }
  };

  const handleDisconnect = async () => {
    setShowDisconnectConfirm(false);
    try {
      await settingsService.disconnectCalendar();
      setOAuthStatus(prev => prev ? {...prev, calendarConnected: false, calendarEmail: undefined} : null);
      setAvailableCalendars([]);
      setUserCalendars([]);
      setReminders([]);
    } catch (error) {
      console.error('Failed to disconnect', error);
    }
  };

  const handleAddCalendar = async (googleCalendarId: string) => {
    setIsAddingCalendar(googleCalendarId);
    try {
      const calendar = await calendarService.addCalendar(googleCalendarId);
      setUserCalendars(prev => [...prev, calendar]);
      setShowAddCalendarDropdown(false);
      // Refresh reminders to include new calendar events
      const events = await calendarService.getReminders(14);
      setReminders(events);
    } catch (error) {
      console.error('Failed to add calendar', error);
    } finally {
      setIsAddingCalendar(null);
    }
  };

  const handleToggleCalendar = async (calendar: Calendar) => {
    try {
      const updated = await calendarService.updateCalendar(
        calendar.id,
        !calendar.isActive,
      );
      setUserCalendars(prev =>
        prev.map(c => (c.id === updated.id ? updated : c)),
      );
      // Refresh reminders after toggle
      const events = await calendarService.getReminders(14);
      setReminders(events);
    } catch (error) {
      console.error('Failed to toggle calendar', error);
    }
  };

  const handleRemoveCalendar = async (calendarId: string) => {
    try {
      await calendarService.removeCalendar(calendarId);
      setUserCalendars(prev => prev.filter(c => c.id !== calendarId));
      // Refresh reminders after removal
      const events = await calendarService.getReminders(14);
      setReminders(events);
    } catch (error) {
      console.error('Failed to remove calendar', error);
    }
  };

  const addedCalendarIds = new Set(userCalendars.map(c => c.googleCalendarId));
  const notAddedCalendars = availableCalendars.filter(
    c => !addedCalendarIds.has(c.id),
  );

  // Group reminders by day
  const groupReminders = (items: Reminder[]): GroupedReminders => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    const groups: GroupedReminders = {
      today: [],
      tomorrow: [],
      thisWeek: [],
      later: [],
    };

    items.forEach(reminder => {
      const date = new Date(reminder.date);
      date.setHours(0, 0, 0, 0);

      if (date.getTime() === today.getTime()) {
        groups.today.push(reminder);
      } else if (date.getTime() === tomorrow.getTime()) {
        groups.tomorrow.push(reminder);
      } else if (date < nextWeek) {
        groups.thisWeek.push(reminder);
      } else {
        groups.later.push(reminder);
      }
    });

    return groups;
  };

  const groupedReminders = groupReminders(reminders);

  if (isLoading && loadError !== 'network') {
    return (
      <SettingsLayout currentScreen="calendar" onNavigate={navigateToSettingsScreen} onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </SettingsLayout>
    );
  }

  const isConnected = oauthStatus?.calendarConnected ?? false;

  // Offline and couldn't load data
  if (loadError === 'network' && !isConnected) {
    return (
      <SettingsLayout currentScreen="calendar" onNavigate={navigateToSettingsScreen} onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <OfflineBanner />
          <BodyText style={{color: theme.textMuted, textAlign: 'center', marginTop: 8}}>{t('common.offlineCannotLoad')}</BodyText>
        </View>
      </SettingsLayout>
    );
  }

  // Not connected state
  if (!isConnected) {
    return (
      <SettingsLayout currentScreen="calendar" onNavigate={navigateToSettingsScreen} onLogout={handleLogout}>
        <ConnectServiceCard
          icon="calendar_month"
          title={t('calendarSettings.connectCalendar')}
          description={t('calendarSettings.connectDescription')}
          buttonLabel={t('calendarSettings.connect')}
          onConnect={handleConnect}
          disabled={!isOnline}
        />
      </SettingsLayout>
    );
  }

  // Connected state with two-column layout
  return (
    <SettingsLayout currentScreen="calendar" onNavigate={navigateToSettingsScreen} onLogout={handleLogout}>
      <View style={styles.pageContainer}>
        <View style={{paddingHorizontal: 24, paddingTop: 16}}>
          <OfflineBanner />
        </View>

        {/* Header */}
        <View style={[styles.header, !isTwoColumn && styles.headerMobile]}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <Heading>{t('calendarSettings.title')}</Heading>
              <View style={styles.connectionStatus}>
                <View style={styles.statusDot} />
                <Text style={styles.statusText}>{t('calendarSettings.connectedTo')}</Text>
              </View>
            </View>
            <View style={styles.headerActions}>
              <TouchableOpacity
                style={[styles.syncButton, !isOnline && {opacity: 0.5}]}
                onPress={handleSync}
                disabled={isSyncing || !isOnline}
                activeOpacity={0.7}>
                {isSyncing ? (
                  <ActivityIndicator size="small" color={theme.primary} />
                ) : (
                  <>
                    <Text style={styles.syncIcon}>sync</Text>
                    <Text style={styles.syncText}>{t('calendarSettings.syncNow')}</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Main Content */}
        <ScrollView
          style={styles.mainContentScroll}
          contentContainerStyle={[styles.mainContent, isTwoColumn && styles.mainContentTwoColumn]}
          refreshControl={
            <RefreshControl {...refreshControlProps} />
          }>
          {/* Left Sidebar */}
          <View style={[styles.sidebar, isTwoColumn && styles.sidebarFixed]}>
            {/* Connected Services */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionIcon}>cloud</Text>
                <SubHeading>{t('calendarSettings.connectedServices')}</SubHeading>
              </View>

              <View style={styles.serviceItem}>
                <View style={styles.serviceInfo}>
                  <View style={styles.serviceIconBg}>
                    <Text style={styles.serviceIcon}>event</Text>
                  </View>
                  <View style={styles.serviceDetails}>
                    <Text style={styles.serviceName}>{t('calendarSettings.googleCalendar')}</Text>
                    <Text style={styles.serviceEmail}>{oauthStatus?.calendarEmail || t('common.connected')}</Text>
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.deleteButton}
                  onPress={() => setShowDisconnectConfirm(true)}
                  activeOpacity={0.7}>
                  <Text style={styles.deleteIcon}>delete</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Visible Calendars */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionIcon}>visibility</Text>
                <SubHeading>{t('calendarSettings.visibleCalendars')}</SubHeading>
              </View>

              {userCalendars.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyText}>{t('calendarSettings.noCalendarsAdded')}</Text>
                </View>
              ) : (
                userCalendars.map(calendar => (
                  <View key={calendar.id} style={styles.calendarItem}>
                    <View style={styles.calendarLeft}>
                      <View
                        style={[
                          styles.calendarColor,
                          {backgroundColor: calendar.calendarColor || theme.primary},
                        ]}
                      />
                      <Text style={styles.calendarName} numberOfLines={1}>
                        {calendar.calendarName || 'Calendar'}
                      </Text>
                    </View>
                    <View style={styles.calendarActions}>
                      <Switch
                        value={calendar.isActive}
                        onValueChange={() => handleToggleCalendar(calendar)}
                        trackColor={{
                          false: theme.borderLight,
                          true: theme.primaryLight,
                        }}
                        thumbColor={calendar.isActive ? theme.primary : theme.textMuted}
                      />
                      <TouchableOpacity
                        style={styles.removeCalendarButton}
                        onPress={() => handleRemoveCalendar(calendar.id)}
                        activeOpacity={0.7}>
                        <Text style={styles.removeCalendarIcon}>close</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))
              )}

              {/* Add Calendar Button/Dropdown */}
              {notAddedCalendars.length > 0 && (
                <View style={styles.addCalendarContainer}>
                  <TouchableOpacity
                    style={styles.addCalendarButton}
                    onPress={() => setShowAddCalendarDropdown(!showAddCalendarDropdown)}
                    activeOpacity={0.7}>
                    <Text style={styles.addCalendarIcon}>add</Text>
                    <Text style={styles.addCalendarText}>{t('calendarSettings.addCalendar')}</Text>
                    <Text style={[styles.addCalendarIcon, styles.chevron]}>
                      {showAddCalendarDropdown ? 'expand_less' : 'expand_more'}
                    </Text>
                  </TouchableOpacity>

                  {showAddCalendarDropdown && (
                    <View style={styles.dropdown}>
                      {notAddedCalendars.map(calendar => (
                        <TouchableOpacity
                          key={calendar.id}
                          style={styles.dropdownItem}
                          onPress={() => handleAddCalendar(calendar.id)}
                          disabled={isAddingCalendar === calendar.id}
                          activeOpacity={0.7}>
                          <View
                            style={[
                              styles.calendarColor,
                              {backgroundColor: calendar.backgroundColor || theme.primary},
                            ]}
                          />
                          <Text style={styles.dropdownItemText}>{calendar.summary}</Text>
                          {isAddingCalendar === calendar.id && (
                            <ActivityIndicator size="small" color={theme.primary} />
                          )}
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </View>
              )}
            </View>

            {/* Local Events (Coming Soon) */}
            <View style={[styles.section, styles.sectionDisabled]}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionIcon, styles.textMuted]}>edit_calendar</Text>
                <SubHeading style={styles.textMuted}>{t('calendarSettings.localEvents')}</SubHeading>
                <View style={styles.comingSoonBadge}>
                  <Text style={styles.comingSoonText}>{t('calendarSettings.comingSoon')}</Text>
                </View>
              </View>
              <Text style={styles.comingSoonDescription}>
                {t('calendarSettings.localEventsDescription')}
              </Text>
            </View>
          </View>

          {/* Right Panel - Events Preview */}
          <View style={[styles.eventsPanel, isTwoColumn && styles.eventsPanelFixed]}>
            <View style={styles.eventsPanelHeader}>
              <Text style={styles.eventsHeaderIcon}>event_note</Text>
              <SubHeading>{t('calendarSettings.eventsPreview')}</SubHeading>
            </View>

            <View style={styles.eventsList}>
              {reminders.length === 0 ? (
                <View style={styles.noEvents}>
                  <Text style={styles.noEventsIcon}>event_busy</Text>
                  <Text style={styles.noEventsText}>{t('calendarSettings.noUpcomingEvents')}</Text>
                  <Text style={styles.noEventsHint}>
                    {t('calendarSettings.noEventsHint')}
                  </Text>
                </View>
              ) : (
                <>
                  {groupedReminders.today.length > 0 && (
                    <EventGroup title={t('calendarSettings.today')} events={groupedReminders.today} allDayText={t('calendarSettings.allDay')} />
                  )}
                  {groupedReminders.tomorrow.length > 0 && (
                    <EventGroup title={t('calendarSettings.tomorrow')} events={groupedReminders.tomorrow} allDayText={t('calendarSettings.allDay')} />
                  )}
                  {groupedReminders.thisWeek.length > 0 && (
                    <EventGroup title={t('calendarSettings.thisWeek')} events={groupedReminders.thisWeek} showDate allDayText={t('calendarSettings.allDay')} />
                  )}
                  {groupedReminders.later.length > 0 && (
                    <EventGroup title={t('calendarSettings.upcoming')} events={groupedReminders.later} showDate allDayText={t('calendarSettings.allDay')} />
                  )}
                </>
              )}
            </View>
          </View>
        </ScrollView>

        {/* Disconnect Confirmation Modal */}
        <Modal
          visible={showDisconnectConfirm}
          transparent
          animationType="fade"
          onRequestClose={() => setShowDisconnectConfirm(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalIconBg}>
                <Text style={styles.modalIcon}>warning</Text>
              </View>
              <Text style={styles.modalTitle}>{t('calendarSettings.confirmDisconnect')}</Text>
              <Text style={styles.modalDescription}>
                {t('calendarSettings.confirmDisconnectDescription')}
              </Text>
              <View style={styles.modalActions}>
                <TouchableOpacity
                  style={styles.modalCancelButton}
                  onPress={() => setShowDisconnectConfirm(false)}
                  activeOpacity={0.7}>
                  <Text style={styles.modalCancelText}>{t('calendarSettings.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.modalConfirmButton}
                  onPress={handleDisconnect}
                  activeOpacity={0.7}>
                  <Text style={styles.modalConfirmText}>{t('calendarSettings.disconnect')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    </SettingsLayout>
  );
}

// Helper to format event time in local timezone
function formatEventTime(event: Reminder): string {
  if (event.isAllDay || !event.startTime) {
    return '';
  }
  const date = new Date(event.startTime);
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Helper to format event date in local timezone
function formatEventDate(event: Reminder): string {
  if (event.startTime) {
    const date = new Date(event.startTime);
    return date.toLocaleDateString(undefined, {weekday: 'short', day: 'numeric'});
  }
  return new Date(event.date + 'T12:00:00').toLocaleDateString(undefined, {weekday: 'short', day: 'numeric'});
}

// Event Group Component
function EventGroup({title, events, showDate, allDayText = 'All day'}: {title: string; events: Reminder[]; showDate?: boolean; allDayText?: string}) {
  return (
    <View style={styles.eventGroup}>
      <Text style={styles.eventGroupTitle}>{title}</Text>
      {events.map((event, index) => (
        <View key={event.id || index} style={styles.eventItem}>
          <View style={styles.eventTimeline}>
            <View
              style={[
                styles.eventDot,
                {backgroundColor: event.calendarColor || theme.primary},
              ]}
            />
            {index < events.length - 1 && <View style={styles.eventLine} />}
          </View>
          <View style={styles.eventContent}>
            <Text style={styles.eventTime}>
              {showDate && (
                <Text style={styles.eventDate}>
                  {formatEventDate(event)}
                  {' · '}
                </Text>
              )}
              {event.isAllDay ? allDayText : formatEventTime(event) || allDayText}
            </Text>
            <Text style={styles.eventTitle}>{event.title}</Text>
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pageContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  // Header
  header: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: theme.borderLight,
  },
  headerMobile: {
    paddingTop: 72,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 16,
  },
  headerLeft: {
    flex: 1,
    minWidth: 200,
  },
  connectionStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.success,
  },
  statusText: {
    fontSize: 14,
    color: theme.textMuted,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },
  syncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: theme.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  syncIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: theme.primary,
  },
  syncText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.text,
  },

  // Main Content
  mainContentScroll: {
    flex: 1,
  },
  mainContent: {
    flexGrow: 1,
    flexDirection: 'column',
  },
  mainContentTwoColumn: {
    flexDirection: 'row',
    minHeight: '100%',
  },

  // Sidebar
  sidebar: {
    padding: 24,
  },
  sidebarFixed: {
    width: 420,
    maxWidth: 420,
    borderRightWidth: 1,
    borderRightColor: theme.borderLight,
  },

  // Section
  section: {
    marginBottom: 32,
  },
  sectionDisabled: {
    opacity: 0.6,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  sectionIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
  },

  // Service Item
  serviceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.surface,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  serviceInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  serviceIconBg: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  serviceIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
  },
  serviceDetails: {
    flex: 1,
  },
  serviceName: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.text,
  },
  serviceEmail: {
    fontSize: 13,
    color: theme.textMuted,
    marginTop: 2,
  },
  deleteButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: theme.errorBg,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: theme.error,
  },

  // Calendar Item
  calendarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: theme.surface,
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  calendarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  calendarColor: {
    width: 14,
    height: 14,
    borderRadius: 4,
  },
  calendarName: {
    fontSize: 15,
    color: theme.text,
    flex: 1,
  },
  calendarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  removeCalendarButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: theme.errorBg,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeCalendarIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 16,
    color: theme.error,
  },

  // Add Calendar
  addCalendarContainer: {
    marginTop: 8,
  },
  addCalendarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: theme.surfaceLight,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: theme.border,
  } as any,
  addCalendarIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
  },
  addCalendarText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.primary,
    flex: 1,
  },
  chevron: {
    color: theme.textMuted,
  },
  dropdown: {
    marginTop: 8,
    backgroundColor: theme.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.borderLight,
    overflow: 'hidden',
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.borderLight,
  },
  dropdownItemText: {
    fontSize: 14,
    color: theme.text,
    flex: 1,
  },

  // Coming Soon
  comingSoonBadge: {
    backgroundColor: theme.surfaceLight,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginLeft: 'auto',
  },
  comingSoonText: {
    fontSize: 11,
    fontWeight: '600',
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  comingSoonDescription: {
    fontSize: 14,
    color: theme.textMuted,
    fontStyle: 'italic',
  },
  textMuted: {
    color: theme.textMuted,
  },

  // Empty State
  emptyState: {
    padding: 20,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: theme.textMuted,
  },

  // Events Panel
  eventsPanel: {
    padding: 24,
    backgroundColor: theme.surfaceLight,
  },
  eventsPanelFixed: {
    flex: 1,
    minWidth: 300,
  },
  eventsPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
  },
  eventsHeaderIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
  },
  eventsList: {
    // Events content
  },

  // No Events
  noEvents: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  noEventsIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: theme.textMuted,
    marginBottom: 16,
  },
  noEventsText: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.textMuted,
    marginBottom: 8,
  },
  noEventsHint: {
    fontSize: 14,
    color: theme.textSubtle,
    textAlign: 'center',
  },

  // Event Group
  eventGroup: {
    marginBottom: 24,
  },
  eventGroupTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },

  // Event Item
  eventItem: {
    flexDirection: 'row',
    minHeight: 50,
  },
  eventTimeline: {
    width: 24,
    alignItems: 'center',
  },
  eventDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginTop: 4,
  },
  eventLine: {
    width: 2,
    flex: 1,
    backgroundColor: theme.borderLight,
    marginTop: 4,
    marginBottom: 4,
  },
  eventContent: {
    flex: 1,
    paddingLeft: 12,
    paddingBottom: 16,
  },
  eventTime: {
    fontSize: 12,
    color: theme.textMuted,
    marginBottom: 4,
  },
  eventDate: {
    fontWeight: '500',
  },
  eventTitle: {
    fontSize: 15,
    color: theme.text,
    fontWeight: '500',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    backgroundColor: theme.surface,
    borderRadius: 20,
    padding: 32,
    alignItems: 'center',
    maxWidth: 400,
    width: '100%',
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  modalIconBg: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: theme.errorBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 32,
    color: theme.error,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.text,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 15,
    color: theme.textMuted,
    textAlign: 'center',
    marginBottom: 28,
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: theme.surfaceLight,
    borderWidth: 1,
    borderColor: theme.border,
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.text,
  },
  modalConfirmButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: theme.error,
    alignItems: 'center',
  },
  modalConfirmText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
});
